import {
  Badge,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title3,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  DEFAULT_REPORT_SCHEDULE_FIELDS,
  formatTimeOfDay,
  type ReportScheduleInput,
  type ReportScheduleRun,
  type StoredReportSchedule,
} from '@worklogix/shared';
import { useCallback, useEffect, useState } from 'react';

import { MOBILE, SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import { ReportBuilder, type ReportBuilderMode } from './reports/ReportBuilder';

/**
 * Admin → **Reports** (Michael, 2026-10-07): "Instead of having a scheduled reports tab
 * in WLX, can you make it the 'reports' tab where we can either create a scheduled
 * report, or run a one off report?"
 *
 * This screen is the LIST and the two entry points; {@link ReportBuilder} is the
 * configuration surface both of them open, so a one-off and a schedule are described
 * by one form and previewed by one renderer.
 *
 * 🔥 THE BUILDER REPLACES THE LIST RATHER THAN OPENING IN A MODAL, which the schedule
 * form used to do. A modal that is wide enough to hold a form AND a live document is
 * not a modal any more — it is the page with a dimmed border — and on a phone it was
 * a form squeezed into half a screen. The list is one click away and nothing is lost.
 *
 * WHAT THIS SCREEN IS CAREFUL ABOUT is the gap between "saved" and "delivered". An
 * admin may type any address; whether mail may actually go there is an ops-controlled
 * allow-list they cannot see or edit. So the run history is shown on the card rather
 * than hidden behind a detail view: a report refused every recipient looks EXACTLY
 * like a working one until somebody reads that line.
 */

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', ...shorthands.gap('16px'), ...SHRINKABLE },
  header: { display: 'flex', alignItems: 'center', ...shorthands.gap('12px'), flexWrap: 'wrap' },
  cards: { display: 'flex', flexDirection: 'column', ...shorthands.gap('12px') },
  card: {
    ...shorthands.padding('14px'),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    ...shorthands.gap('12px'),
    alignItems: 'flex-start',
    [MOBILE]: { flexDirection: 'column', alignItems: 'stretch' },
  },
  cardMain: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    flexGrow: 1,
    minWidth: 0,
  },
  cardName: { fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  actions: { display: 'flex', ...shorthands.gap('8px'), flexShrink: 0 },
  empty: {
    ...shorthands.padding('24px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
});

function blankSchedule(): ReportScheduleInput {
  return {
    name: '',
    active: true,
    frequency: 'weekly',
    dayOfWeek: 3, // Wednesday — the payroll week rolls Wed→Tue
    dayOfMonth: null,
    sendAtMinutes: 7 * 60,
    recipients: [],
    scope: 'all',
    employeeIds: [],
    departmentIds: [],
    fields: [...DEFAULT_REPORT_SCHEDULE_FIELDS],
    sections: [],
  };
}

/** Plain English, so the card says what it does without anyone decoding a cron line. */
function describe(s: StoredReportSchedule | ReportScheduleInput): string {
  const at = formatTimeOfDay(s.sendAtMinutes);
  const when =
    s.frequency === 'daily'
      ? `Every day at ${at}`
      : s.frequency === 'weekly'
        ? `Every ${DAY_NAMES[s.dayOfWeek ?? 0] ?? '—'} at ${at}`
        : `Day ${String(s.dayOfMonth ?? 1)} of each month at ${at}`;
  const who =
    s.scope === 'all'
      ? 'all employees'
      : s.scope === 'supervisor'
        ? 'all employees, split by supervisor'
        : 'selected employees';
  return `${when} · ${who}`;
}

interface Editing {
  mode: ReportBuilderMode;
  /** The schedule being edited; null for a new schedule or a one-off. */
  id: string | null;
  draft: ReportScheduleInput;
}

export function ReportsTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [schedules, setSchedules] = useState<StoredReportSchedule[]>([]);
  const [employees, setEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [departments, setDepartments] = useState<Array<{ id: string; name: string }>>([]);
  const [runs, setRuns] = useState<Map<string, ReportScheduleRun[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, emps, depts] = await Promise.all([
        api.listReportSchedules(),
        api.listEmployees(),
        api.listDepartments(),
      ]);
      setSchedules(rows);
      setEmployees(
        emps
          .map((e) => ({ id: e.id, name: e.displayName }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
      setDepartments(
        depts.map((d) => ({ id: d.id, name: d.name })).sort((a, b) => a.name.localeCompare(b.name))
      );
      setError(null);
      // The run history is what distinguishes "sent" from "refused", so it is fetched
      // with the list rather than on demand — nobody clicks into a report that looks
      // like it is working.
      const pairs = await Promise.all(
        rows.map(async (r) => {
          try {
            return [r.id, await api.reportScheduleRuns(r.id)] as const;
          } catch {
            return [r.id, [] as ReportScheduleRun[]] as const;
          }
        })
      );
      setRuns(new Map(pairs));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the reports.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (change: Partial<ReportScheduleInput>): void => {
    setEditing((prev) => (prev === null ? prev : { ...prev, draft: { ...prev.draft, ...change } }));
  };

  const save = async (): Promise<void> => {
    if (editing === null) {
      return;
    }
    setSaving(true);
    try {
      if (editing.id === null) {
        await api.createReportSchedule(editing.draft);
        setNotice(`"${editing.draft.name}" will send ${describe(editing.draft).toLowerCase()}.`);
      } else {
        await api.updateReportSchedule(editing.id, editing.draft);
        setNotice(`"${editing.draft.name}" updated.`);
      }
      setEditing(null);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The schedule could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s: StoredReportSchedule): Promise<void> => {
    setSaving(true);
    try {
      await api.deleteReportSchedule(s.id);
      setNotice(`"${s.name}" deleted.`);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The schedule could not be deleted.');
    } finally {
      setSaving(false);
    }
  };

  if (editing !== null) {
    return (
      <ReportBuilder
        mode={editing.mode}
        scheduleId={editing.id}
        draft={editing.draft}
        onDraftChange={patch}
        employees={employees}
        departments={departments}
        onCancel={() => {
          setEditing(null);
        }}
        onSave={save}
        saving={saving}
      />
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>Reports</Title3>
        <Button
          appearance="primary"
          onClick={() => {
            setEditing({ mode: 'oneOff', id: null, draft: blankSchedule() });
          }}
        >
          Run a report now
        </Button>
        <Button
          onClick={() => {
            setEditing({ mode: 'schedule', id: null, draft: blankSchedule() });
          }}
        >
          New scheduled report
        </Button>
      </div>

      <Text className={styles.muted}>
        Run a report once and download it, or schedule one to be emailed automatically.
        Scheduled delivery is limited to addresses your IT administrator has allowed — if a
        recipient is refused, it is shown on the report below rather than failing quietly.
      </Text>

      {error !== null ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {notice !== null ? (
        <MessageBar intent="success">
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      ) : null}

      {loading ? (
        <Spinner label="Loading reports…" />
      ) : schedules.length === 0 ? (
        <div className={styles.empty}>
          <Text>
            No scheduled reports yet. Run one now to see what it contains, or create a
            schedule and it will send on its own.
          </Text>
        </div>
      ) : (
        <div className={styles.cards}>
          {schedules.map((s) => {
            const history = runs.get(s.id) ?? [];
            const last = history[0];
            return (
              <div key={s.id} className={styles.card}>
                <div className={styles.cardMain}>
                  <Text className={styles.cardName}>
                    {s.name} {s.active ? null : <Badge color="informative">Paused</Badge>}
                  </Text>
                  <Text className={styles.muted}>{describe(s)}</Text>
                  <Text className={styles.muted}>To: {s.recipients.join(', ')}</Text>
                  {/*
                    The last run, stated plainly. A report whose recipients were all
                    refused has "sent" nothing, and that is the case most likely to be
                    mistaken for success — so it gets a warning badge, not a quiet line.
                  */}
                  {last === undefined ? (
                    <Text className={styles.muted}>Has not run yet.</Text>
                  ) : last.outcome === 'sent' ? (
                    <Text className={styles.muted}>
                      Last sent {last.runOn} to {last.sentTo.length} recipient
                      {last.sentTo.length === 1 ? '' : 's'}
                      {last.rowCount === null ? '' : ` · ${String(last.rowCount)} entries`}
                    </Text>
                  ) : (
                    <MessageBar intent={last.outcome === 'failed' ? 'error' : 'warning'}>
                      <MessageBarBody>
                        {last.runOn}: {last.problem ?? 'Did not send.'}
                        {last.blocked.length > 0 ? ` Refused: ${last.blocked.join(', ')}.` : ''}
                      </MessageBarBody>
                    </MessageBar>
                  )}
                </div>
                <div className={styles.actions}>
                  <Button
                    onClick={() => {
                      setEditing({
                        mode: 'schedule',
                        id: s.id,
                        draft: {
                          name: s.name,
                          active: s.active,
                          frequency: s.frequency,
                          dayOfWeek: s.dayOfWeek,
                          dayOfMonth: s.dayOfMonth,
                          sendAtMinutes: s.sendAtMinutes,
                          recipients: [...s.recipients],
                          scope: s.scope,
                          employeeIds: [...s.employeeIds],
                          departmentIds: [...s.departmentIds],
                          fields: [...s.fields],
                          sections: [...s.sections],
                        },
                      });
                    }}
                  >
                    Edit
                  </Button>
                  {/*
                    The same saved configuration, run for a window of the admin's
                    choosing. The commonest real question about a scheduled report is
                    "what did this look like last week?", and before this the only way
                    to answer it was to wait for Wednesday.
                  */}
                  <Button
                    onClick={() => {
                      setEditing({
                        mode: 'oneOff',
                        id: null,
                        draft: {
                          name: s.name,
                          active: s.active,
                          frequency: s.frequency,
                          dayOfWeek: s.dayOfWeek,
                          dayOfMonth: s.dayOfMonth,
                          sendAtMinutes: s.sendAtMinutes,
                          recipients: [...s.recipients],
                          scope: s.scope,
                          employeeIds: [...s.employeeIds],
                          departmentIds: [...s.departmentIds],
                          fields: [...s.fields],
                          sections: [...s.sections],
                        },
                      });
                    }}
                  >
                    Run now
                  </Button>
                  <Button disabled={saving} onClick={() => void remove(s)}>
                    Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
